import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Activity,
  Bot,

  Route,
  Wrench,
  Calculator,
  Landmark,
  FileText,
  MapPin,
  Video,
  StickyNote,
  ChevronDown,
  User,
  Crown,
  HelpCircle,
  CreditCard,
  Scale,
  LifeBuoy,
  Shield,
  LogOut,
  Home,
  Pencil,
  Lock,
  Thermometer,
  Info,
  ArrowLeft,
  Store,
  LogIn,
  X,
  Megaphone,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { SectorIcon } from "@/components/SectorOnboardingModal";
import MarkerHighlight from "@/components/MarkerHighlight";
import VipMentoringPanel from "@/components/panel/VipMentoringPanel";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

/** Recuerda si el grupo Home quedó abierto en este navegador. */
const HOME_OPEN_KEY = "rl-panel-home-open";
const TOOLS_OPEN_KEY = "rl-panel-tools-open";
/** Recuerda si el usuario ya pulsó "Nueva Consulta" o envió su primera duda. */
const HIGHLIGHT_DISMISSED_KEY = "rl-new-consult-highlight-dismissed";


type Item = {
  tab: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Si existe, el elemento navega a esta ruta en vez de cambiar de pestaña. */
  href?: string;
};

export interface PanelSidebarProps {
  activeTab: string;
  onSelectTab: (tab: string) => void;
  lang: "es" | "en";
  /** Global checklist progress (0-100). */
  progress: number;
  storeName: string;
  planLabel: string;
  /** Raw sector key from the profile (used for icon + category label). */
  retailSector?: string;
  /** Mentoring sessions left this cycle, when the user has mentoring. */
  sessionsLabel?: string;
  /** Mentoring wallet minutes, when available. */
  minutesRemaining?: number;
  minutesTotal?: number;
  /** Whether the current user is an admin (shows the back office link). */
  isAdmin?: boolean;
  /** Rol real de administrador: el enlace al back office se ve aunque simule otro plan. */
  showAdminLink?: boolean;
  /** Change the interface language. */
  onSetLang?: (lang: "es" | "en") => void;
  onScheduleSession?: () => void;
  /** Buy one-off extra hours (Stripe one-time checkout). */
  onAddHours?: () => void;
  onSeeMentoring?: () => void;

  /** Visita pública (sin cuenta): cabecera editable y pie de conversión. */
  publicMode?: boolean;
  /** Guarda el nombre del negocio escrito por el visitante. */
  onSetStoreName?: (name: string) => void;
  /** Resalta un instante el campo del negocio (Sara acaba de guardarlo). */
  highlightStoreName?: boolean;
  /** Abre el alta gratuita (plan Lite). */
  onRegister?: () => void;
  /** Visita pública: abre la ventana de acceso en modo inicio de sesión. */
  onLogin?: () => void;

  /** Abre la suscripción al Club Pro. */
  onJoinPro?: () => void;
  /** Desplaza a una sección de la home pública. */
  onGoHomeSection?: (anchor: string) => void;
}

const PanelSidebar = ({
  activeTab,
  onSelectTab,
  lang,
  progress,
  storeName,
  planLabel,
  retailSector,
  minutesRemaining,
  minutesTotal,
  isAdmin,
  showAdminLink,
  onSetLang,
  onScheduleSession,
  onAddHours,
  onSeeMentoring,
  publicMode = false,
  onSetStoreName,
  highlightStoreName = false,
  onRegister,
  onLogin,

  onJoinPro,
  onGoHomeSection,
}: PanelSidebarProps) => {
  const es = lang === "es";
  const { t } = useLanguage();
  const { isMobile, openMobile, setOpenMobile, setOpen, state } = useSidebar();
  const iconOnly = !isMobile && state === "collapsed";

  // Herramientas empieza siempre plegado para mostrar el menú completo.
  const [toolsOpen, setToolsOpen] = useState(false);
  // Home también empieza contraída para que se vea el menú completo de un vistazo.
  const [homeOpen, setHomeOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [vipOpen, setVipOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [expertOpen, setExpertOpen] = useState(false);
  const [highlightDismissed, setHighlightDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(HIGHLIGHT_DISMISSED_KEY) === "1";
  });

  const dismissHighlight = useCallback(() => {
    setHighlightDismissed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(HIGHLIGHT_DISMISSED_KEY, "1");
    }
  }, []);

  const closeSidebar = useCallback(() => {
    setAccountOpen(false);
    setVipOpen(false);
    setExpertOpen(false);
    if (isMobile) setOpenMobile(false);
    else setOpen(false);
  }, [isMobile, setOpen, setOpenMobile]);

  const changeLanguage = useCallback((next: "es" | "en") => {
    if (!onSetLang || next === lang) return;
    setAccountOpen(false);
    setVipOpen(false);
    setExpertOpen(false);
    if (isMobile) setOpenMobile(false);
    else setOpen(false);

    window.setTimeout(() => {
      onSetLang(next);
    }, isMobile ? 320 : 220);
  }, [isMobile, lang, onSetLang, setOpen, setOpenMobile]);

  // Desaparece el destacado cuando el usuario entra en Nueva Consulta.
  const prevActiveTab = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevActiveTab.current !== undefined && prevActiveTab.current !== "sara" && activeTab === "sara") {
      dismissHighlight();
    }
    prevActiveTab.current = activeTab;
  }, [activeTab, dismissHighlight]);

  // También se desactiva si Sara confirma que se envió una consulta.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => dismissHighlight();
    window.addEventListener("rl-new-consult-used", handler);
    return () => window.removeEventListener("rl-new-consult-used", handler);
  }, [dismissHighlight]);

  const homeSections: { anchor: string; label: string }[] = [
    { anchor: "como-funciona", label: es ? "Cómo funciona" : "How it works" },
    { anchor: "estrategia", label: es ? "La estrategia" : "The strategy" },
    { anchor: "agente-ia", label: es ? "Agente Retail IA" : "Retail AI Agent" },
    { anchor: "tarifas", label: es ? "Tarifas" : "Pricing" },
  ];

  const mentoringLabel = es ? "Pregunta a un experto" : "Ask an expert";
  const progressHelp = es
    ? "Te acompañamos semana a semana para que sepas qué mejorar y qué paso dar después, sin listas eternas ni tecnicismos."
    : "We guide you week by week so you know what to improve and what to do next, without endless lists or jargon.";

  


    const main: Item[] = [
      { tab: "sara", label: t("sidebar", "agentRetailIA"), icon: Bot },
      { tab: "roadmap", label: es ? "Hoja de Ruta" : "Roadmap", icon: Route },
    ];


  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(TOOLS_OPEN_KEY) === "1") setToolsOpen(true);
  }, []);

  const changeToolsOpen = (open: boolean) => {
    setToolsOpen(open);
    if (typeof window !== "undefined") window.localStorage.setItem(TOOLS_OPEN_KEY, open ? "1" : "0");
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(HOME_OPEN_KEY) === "1") setHomeOpen(true);
  }, []);

  const changeHomeOpen = (open: boolean) => {
    setHomeOpen(open);
    if (typeof window !== "undefined") window.localStorage.setItem(HOME_OPEN_KEY, open ? "1" : "0");
  };

  /** "Home" lleva a la portada pública, arriba del todo. */
  const goHomeTop = () => {
    onSelectTab("home");
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    if (isMobile) setOpenMobile(false);
  };

  const tools: Item[] = [
    { tab: "calculator", label: es ? "Calculadora" : "Calculator", icon: Calculator },
    {
      tab: "kpis",
      label: "KPIs",
      icon: Activity,
      ...(publicMode ? { href: es ? "/herramientas/kpis" : "/en/tools/kpis" } : {}),
    },

    // Subvenciones tiene una única dirección pública, dentro y fuera del panel.
    {
      tab: "grants",
      label: es ? "Subvenciones" : "Grants",
      icon: Landmark,
      href: es ? "/recursos/subvenciones" : "/en/resources/subsidies",
    },
    {
      tab: "proposals",
      label: es ? "Proveedores" : "Suppliers",
      icon: FileText,
      ...(publicMode ? { href: es ? "/herramientas/proveedores" : "/en/tools/suppliers" } : {}),
    },
    { tab: "locations", label: es ? "Mis Locales" : "My Locations", icon: MapPin },
    // En visita pública tiene URL propia indexable; dentro del panel privado
    // sigue siendo una pestaña para no romper el flujo.
    {
      tab: "thermometer",
      label: es ? "Detector de Fugas" : "Leak Detector",
      icon: Thermometer,
      ...(publicMode ? { href: es ? "/detector-de-fugas" : "/en/leak-detector" } : {}),
    },
    {
      tab: "marketing",
      label: "Marketing",
      icon: Megaphone,
      ...(publicMode ? { href: "/marketing" } : {}),
    },
  ];
  // Visita pública: cada sección de contenido tiene URL propia e indexable.
  // Dentro del panel privado siguen siendo pestañas para no romper el flujo.
  const extras: Item[] = [
    {
      tab: "tips",
      label: es ? "Consejos e Ideas" : "Tips & Ideas",
      icon: StickyNote,
      ...(publicMode ? { href: es ? "/consejos" : "/en/tips" } : {}),
    },
    {
      tab: "casos",
      label: es ? "Casos Reales" : "Real Cases",
      icon: Video,
      ...(publicMode ? { href: es ? "/casos-reales" : "/en/real-cases" } : {}),
    },
    {
      tab: "answers",
      label: es ? "Consultas Resueltas" : "Answers",
      icon: HelpCircle,
      ...(publicMode ? { href: es ? "/consultas-resueltas" : "/en/answers" } : {}),
    },
  ];

  const pick = (tab: string) => {
    onSelectTab(tab);
    if (isMobile) setOpenMobile(false);
  };

  const closeAccount = () => {
    setAccountOpen(false);
    if (isMobile) setOpenMobile(false);
  };


  const subLinkCls =
    "flex min-h-11 items-center gap-2 rounded-lg px-2 py-1.5 font-body text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground";

  /** Clases comunes: en modo icono el botón es un cuadrado centrado sin sangrías. */
  const itemCls = (active: boolean, variant: "main" | "tool") =>
    cn(
      "min-h-11 font-body",
      variant === "main" ? "text-sm" : "text-[13px]",
      "group-data-[collapsible=icon]:min-h-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0",
      active && "bg-primary/15 text-foreground font-semibold border-l-2 border-primary",
      variant === "tool" && !active && "text-muted-foreground",
    );

  const itemIconCls = (variant: "main" | "tool") =>
    cn(
      "h-4 w-4 shrink-0",
      variant === "tool" && "h-[15px] w-[15px] opacity-80",
      "group-data-[collapsible=icon]:mx-auto",
    );

  const renderItems = (items: Item[], variant: "main" | "tool" = "main") => (
    <SidebarMenu className="gap-0.5">
      {items.map((it) => {
        const active = activeTab === it.tab;
        const showHighlight = it.tab === "sara" && !iconOnly && !highlightDismissed;
        const inner = (
          <>
            <it.icon className={itemIconCls(variant)} aria-hidden="true" />
            <span className="group-data-[collapsible=icon]:hidden">{it.label}</span>
            {showHighlight && (
              <>
                <span className="ml-auto rounded-md bg-primary px-1.5 py-0.5 font-body text-[10px] font-bold text-primary-foreground">
                  {t("sidebar", "tryIt")}
                </span>
                <ArrowLeft
                  className="h-3.5 w-3.5 shrink-0 animate-bounce-left text-foreground"
                  aria-hidden="true"
                />
              </>
            )}
          </>
        );
        return (
          <SidebarMenuItem key={it.tab}>
            <SidebarMenuButton
              {...(it.href ? { asChild: true } : { onClick: () => pick(it.tab) })}
              isActive={active}
              tooltip={it.label}
              className={itemCls(active, variant)}
            >
              {it.href ? (
                <Link to={it.href} onClick={() => { if (isMobile) setOpenMobile(false); }}>
                  {inner}
                </Link>
              ) : (
                inner
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );


  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-1 p-3 pr-2 md:gap-2 md:p-4 md:pr-3 group-data-[collapsible=icon]:p-2">
        <div className="flex min-h-11 items-center gap-2">
          <Link to="/" className="flex min-w-0 flex-1 items-center py-1 text-left transition-opacity hover:opacity-80 group-data-[collapsible=icon]:justify-center">
            <span className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary font-display text-xs font-bold leading-none text-primary-foreground group-data-[collapsible=icon]:flex" aria-hidden="true">RL</span>
            <span className="block truncate font-display text-2xl font-bold tracking-tight leading-none group-data-[collapsible=icon]:hidden">
              RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
            </span>
          </Link>
          <button
            type="button"
            onClick={closeSidebar}
            aria-label={es ? "Cerrar menú" : "Close menu"}
            title={es ? "Cerrar menú" : "Close menu"}
            className="-mr-2.5 ml-auto flex h-11 w-11 shrink-0 items-center justify-center text-foreground/35 transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring group-data-[collapsible=icon]:hidden"
          >
            <X className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        {publicMode ? (
          editingName ? (
            <form
              className="mt-3"
              onSubmit={(e) => {
                e.preventDefault();
                onSetStoreName?.(draftName.trim());
                setEditingName(false);
              }}
            >
              <input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onBlur={() => {
                  onSetStoreName?.(draftName.trim());
                  setEditingName(false);
                }}
                aria-label={es ? "Nombre de tu negocio" : "Your business name"}
                placeholder={es ? "Nombre de tu negocio" : "Your business name"}
                className="w-full min-h-11 rounded-xl border border-border bg-card px-2.5 py-2 font-body text-sm font-semibold outline-none focus:border-primary"
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDraftName(storeName || "");
                setEditingName(true);
              }}
              className={cn(
                "mt-2 flex w-full min-h-11 items-center gap-2 rounded-xl border bg-card px-2.5 py-2 text-left transition-all hover:bg-secondary md:mt-3 group-data-[collapsible=icon]:hidden",
                highlightStoreName
                  ? "border-primary bg-primary/10 ring-2 ring-primary/50"
                  : "border-border",
              )}
            >
              <Store className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate font-body text-sm font-semibold">
                {storeName || (es ? "Tu negocio" : "Your business")}
              </span>
              <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          )
        ) : (
        <Popover open={accountOpen} onOpenChange={setAccountOpen}>
          <PopoverTrigger asChild>
            <button
              data-tour="sidebar-account"
              type="button"
              className="mt-3 flex w-full min-h-11 items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-2 hover:bg-secondary transition-colors group-data-[collapsible=icon]:hidden"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-foreground">
                <SectorIcon sector={retailSector || ""} className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0 flex-1 truncate text-left font-body text-sm font-semibold">
                {storeName || (es ? "Mi cuenta" : "My account")}
              </span>
              <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", accountOpen ? "rotate-180" : "rotate-0")} aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={6}
            className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[15rem] rounded-xl border border-border bg-popover p-1.5 shadow-xl"
          >
            <div className="space-y-0.5">
              <Link className={subLinkCls} to={es ? "/cuenta/perfil" : "/en/account/profile"} onClick={closeAccount}>
                <User className="h-4 w-4 shrink-0" aria-hidden="true" />
                {es ? "Mi Perfil" : "My Profile"}
              </Link>
              <Link className={subLinkCls} to={es ? "/cuenta/facturacion" : "/en/account/billing"} onClick={closeAccount}>
                <CreditCard className="h-4 w-4 shrink-0" aria-hidden="true" />
                {es ? "Mi Suscripción" : "My Subscription"}
              </Link>
              <Link className={subLinkCls} to={es ? "/faq" : "/en/faq"} onClick={closeAccount}>
                <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {es ? "Soporte y FAQs" : "Support & FAQs"}
              </Link>

              <a className={subLinkCls} href="mailto:hola@retaillemon.com?subject=Soporte%20RetailLemon" onClick={closeAccount}>
                <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden="true" />
                {es ? "Contacto" : "Contact"}
              </a>
              <Link className={subLinkCls} to={es ? "/aviso-legal" : "/en/legal-notice"} onClick={closeAccount}>
                <Scale className="h-4 w-4 shrink-0" aria-hidden="true" />
                {es ? "Condiciones y Legal" : "Terms & Legal"}
              </Link>

              {onSetLang && (
                <div className="flex gap-2 px-2 py-2">
                  {(["es", "en"] as const).map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => changeLanguage(l)}
                      aria-pressed={lang === l}
                      className={cn(
                        "rounded-full border px-3 py-1.5 font-body text-xs font-semibold transition-colors",
                        lang === l
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border hover:bg-secondary",
                      )}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}

              {(showAdminLink ?? isAdmin) && (
                <>
                  <div className="my-1 border-t border-border" />
                  <Link className={subLinkCls} to="/admin" onClick={closeAccount}>
                    <Shield className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {es ? "Panel de Administrador" : "Admin panel"}
                  </Link>
                  <div className="my-1 border-t border-border" />
                </>
              )}

              <button
                type="button"
                className={`${subLinkCls} w-full text-left text-destructive hover:text-destructive`}
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.href = "/";
                }}
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
                {es ? "Cerrar Sesión" : "Sign out"}
              </button>
            </div>
          </PopoverContent>
        </Popover>
        )}

        <div className="mt-2 md:mt-3 group-data-[collapsible=icon]:hidden">
          <div className="flex items-center justify-between font-body text-[11px] text-muted-foreground">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">
                {publicMode
                  ? es ? "Progreso de tu diagnóstico" : "Your diagnosis progress"
                  : es ? "Progreso global" : "Global progress"}
              </span>
              {publicMode && (isMobile ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={es ? "Más información sobre tu progreso" : "More information about your progress"}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" side="bottom" className="w-64 p-3 font-body text-xs leading-relaxed">
                    {progressHelp}
                  </PopoverContent>
                </Popover>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={es ? "Más información sobre tu progreso" : "More information about your progress"}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-64 font-body text-xs font-normal leading-relaxed">
                    {progressHelp}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
            <span className="font-semibold text-foreground">{progress}%</span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </SidebarHeader>


      <SidebarContent data-tour="sidebar-nav" className={isMobile ? "min-h-fit flex-none gap-0 overflow-visible" : undefined}>
        {publicMode && (
          <Collapsible open={homeOpen} onOpenChange={changeHomeOpen}>
            <SidebarGroup className="px-2 py-1.5 md:p-2">
              <SidebarMenu className="gap-0.5">
                <SidebarMenuItem className="flex items-center gap-1 group-data-[collapsible=icon]:gap-0">
                  {/* El nombre lleva a la home principal; la flechita abre las secciones. */}
                  <SidebarMenuButton
                    onClick={goHomeTop}
                    isActive={activeTab === "home"}
                    tooltip={es ? "Home" : "Home"}
                    className={itemCls(activeTab === "home", "main")}
                  >
                    <Home className={itemIconCls("main")} aria-hidden="true" />
                    <span className="group-data-[collapsible=icon]:hidden">{es ? "Home" : "Home"}</span>
                  </SidebarMenuButton>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      aria-label={es ? "Ver secciones de la home" : "Show home sections"}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground group-data-[collapsible=icon]:hidden"
                    >
                      <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", homeOpen ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
                    </button>
                  </CollapsibleTrigger>
                </SidebarMenuItem>
              </SidebarMenu>
              <CollapsibleContent className="group-data-[collapsible=icon]:hidden">
                <SidebarGroupContent>
                  <div className="ml-3 space-y-0.5 border-l border-border pl-1.5">
                    {homeSections.map((s) => (
                      <button
                        key={s.anchor}
                        type="button"
                        className={`${subLinkCls} w-full text-left`}
                        onClick={() => {
                          onGoHomeSection?.(s.anchor);
                          if (isMobile) setOpenMobile(false);
                        }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        <SidebarGroup className="border-t border-border/60 px-2 py-1.5 md:p-2">
          <SidebarGroupContent>{renderItems(main)}</SidebarGroupContent>
        </SidebarGroup>


        <Collapsible open={toolsOpen} onOpenChange={changeToolsOpen}>
          <SidebarGroup className="border-t border-border/60 px-2 py-1.5 md:p-2">
            <SidebarMenu className="gap-0.5">
              <SidebarMenuItem className="flex items-center gap-0.5">
                <SidebarMenuButton asChild tooltip={es ? "Herramientas" : "Tools"} className="min-h-9 flex-1 font-body text-xs font-semibold uppercase tracking-wider text-muted-foreground group-data-[collapsible=icon]:min-h-8 group-data-[collapsible=icon]:justify-center">
                  <Link to={es ? "/herramientas" : "/en/tools"} onClick={() => { if (isMobile) setOpenMobile(false); }}>
                    <Wrench className={itemIconCls("main")} aria-hidden="true" />
                    <span className="flex-1 group-data-[collapsible=icon]:hidden">{es ? "Herramientas" : "Tools"}</span>
                  </Link>
                </SidebarMenuButton>
                <button
                  type="button"
                  aria-label={toolsOpen ? (es ? "Ocultar accesos" : "Hide shortcuts") : (es ? "Mostrar accesos" : "Show shortcuts")}
                  aria-expanded={toolsOpen}
                  onClick={() => { if (iconOnly) setOpen(true); changeToolsOpen(iconOnly ? true : !toolsOpen); }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground group-data-[collapsible=icon]:hidden"
                >
                  <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", toolsOpen ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
                </button>
              </SidebarMenuItem>
            </SidebarMenu>
            {/* Menú reducido: los iconos de las herramientas quedan visibles bajo la llave */}
            <div className="hidden group-data-[collapsible=icon]:block">
              <SidebarGroupContent>{renderItems(tools, "tool")}</SidebarGroupContent>
            </div>
            <CollapsibleContent className="group-data-[collapsible=icon]:hidden">
              <SidebarGroupContent>
                <div className="ml-3 border-l border-border pl-1.5">
                  {renderItems(tools, "tool")}
                </div>
              </SidebarGroupContent>
            </CollapsibleContent>

          </SidebarGroup>
        </Collapsible>

        <SidebarGroup className="border-t border-border/60 px-2 py-1.5 md:p-2">
          <SidebarGroupContent>{renderItems(extras)}</SidebarGroupContent>
        </SidebarGroup>


      </SidebarContent>

      <SidebarFooter className="shrink-0 gap-2 px-3 pb-3 pt-2 md:p-3 group-data-[collapsible=icon]:hidden">
        {publicMode ? (
          <>
            <button
              type="button"
              onClick={() => { onRegister?.(); if (isMobile) setOpenMobile(false); }}
              className="flex min-h-11 w-full items-center justify-center rounded-full bg-primary px-4 font-body text-sm font-bold text-primary-foreground transition-all hover:brightness-95"
            >
              {es ? "Acceso anticipado" : "Early access"}
            </button>
            <button
              type="button"
              onClick={() => { onJoinPro?.(); if (isMobile) setOpenMobile(false); }}
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-full border-2 border-primary bg-card px-4 font-body text-sm font-bold text-foreground transition-all hover:bg-secondary"
            >
              <Crown className="h-4 w-4 shrink-0" aria-hidden="true" strokeWidth={1.75} />
              <span className="whitespace-nowrap">{es ? "Únete al Club Pro · 69 €/mes" : "Join Club Pro · €69/mo"}</span>
            </button>

            <Popover open={expertOpen} onOpenChange={setExpertOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 font-body text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                >
                  <Lock className="h-4 w-4 shrink-0" aria-hidden="true" strokeWidth={1.5} />
                  <span className="min-w-0 truncate text-center">{mentoringLabel}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                sideOffset={8}
                className="z-50 w-[16rem] rounded-xl border border-border bg-popover p-3 shadow-xl"
              >
                <p className="font-body text-[13px] leading-snug text-muted-foreground">
                  {t("expertSupport", "guestPopoverPitch")}
                </p>
              </PopoverContent>
            </Popover>

            <div className="flex min-h-11 items-center justify-between gap-2 pt-1">
              {onLogin ? (
                <button
                  type="button"
                  onClick={() => { onLogin(); if (isMobile) setOpenMobile(false); }}
                  className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg pl-0.5 pr-2 text-left font-body text-sm font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <LogIn className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
                  <span className="truncate">{es ? "Iniciar sesión" : "Log in"}</span>
                </button>
              ) : <span />}
              {onSetLang && (
                <div className="flex gap-2">
                  {(["es", "en"] as const).map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => changeLanguage(l)}
                      aria-pressed={lang === l}
                      className={cn(
                        "rounded-full border px-3 py-1.5 font-body text-xs font-semibold transition-colors",
                        lang === l
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border hover:bg-secondary",
                      )}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </>
        ) : (
        <VipMentoringPanel
          open={vipOpen}
          onOpenChange={setVipOpen}
          lang={lang}
          minutesRemaining={minutesRemaining}
          minutesTotal={minutesTotal}
          onBook={() => {
            if (onScheduleSession) onScheduleSession();
            else onSeeMentoring?.();
            if (isMobile) setOpenMobile(false);
          }}
        >
          <button
            type="button"
            aria-label={mentoringLabel}
            className="flex min-h-[88px] w-full items-center gap-3 rounded-2xl border-2 border-primary bg-card px-4 py-3 text-left shadow-sm transition-all hover:bg-secondary hover:shadow-md"
          >
            <Crown className="h-6 w-6 shrink-0 text-foreground" aria-hidden="true" strokeWidth={1.75} />
            <span className="min-w-0 flex-1 truncate font-display text-[17px] font-bold leading-tight text-foreground">
              {mentoringLabel}
            </span>
            <ChevronDown
              className={cn("h-5 w-5 shrink-0 text-foreground transition-transform", vipOpen ? "rotate-180" : "")}
              aria-hidden="true"
            />
          </button>

        </VipMentoringPanel>
        )}
      </SidebarFooter>


    </Sidebar>
  );
};

export default PanelSidebar;
